// =============================================================================
// domain/pnlUnit.ts — which unit every P&L on screen is expressed in.
// -----------------------------------------------------------------------------
// The unit itself is one string in the `app_flags` JSONB blob, and the CONVERSION
// already lives in `domain/trade` (`tradeValue`, `excursionValue`) and
// `domain/kpis`. This file is only the preference: read it, validate it, merge a
// new one back without disturbing the other flags in the blob.
//
// ── ⚠ THE SAME KEY BETA WRITES ──
//
// `appFlags.pnlDisplayUnit`, byte for byte. Both clients point at the same
// database (D5), so a trader who switches to Points in beta finds this client
// already showing Points. Renaming the key would silently give one user two
// different answers to "what unit am I in" depending on which client they opened.
//
// ── 🚨 WHY THE LABEL MAPS ARE KEYED BY THE TYPE ──
//
// The display ORDER is derived from one of them rather than written out a second
// time. A fifth unit added to `PnlUnit` (account-% is the one beta deferred) is
// then a TYPE ERROR here instead of a unit that silently never appears in the
// picker. Same trick, same reason, as `RangePicker`'s preset labels.
// =============================================================================

import type { PnlUnit } from './types';

/** The flag key inside `app_flags`. Beta's spelling — see the header. */
export const PNL_UNIT_FLAG = 'pnlDisplayUnit';

/**
 * The picker's long labels, transcribed from beta.
 *
 * ⚠ `Dollars ($)`, not `Dollars`. The parenthesis is beta's, and it is the only
 * thing in the menu that ties that row to the `$` on the collapsed button.
 */
export const PNL_UNIT_LABELS: Record<PnlUnit, string> = {
  usd: 'Dollars ($)',
  points: 'Points',
  ticks: 'Ticks',
  r: 'R-multiple',
};

/** What the collapsed button shows. Beta's four strings. */
export const PNL_UNIT_BUTTON: Record<PnlUnit, string> = {
  usd: '$',
  points: 'pts',
  ticks: 'ticks',
  r: 'R',
};

/**
 * Display order — derived from the label map, never a second list.
 *
 * ⚠ Insertion order, which the language guarantees for string keys. This is the
 * one place the four units are ordered, so the picker cannot drift out of step
 * with beta's menu.
 */
export const PNL_UNITS = Object.keys(PNL_UNIT_LABELS) as PnlUnit[];

/** Whether an arbitrary stored value is one of the four units. */
export function isPnlUnit(v: unknown): v is PnlUnit {
  return typeof v === 'string' && Object.prototype.hasOwnProperty.call(PNL_UNIT_LABELS, v);
}

/**
 * The stored unit, or dollars.
 *
 * ⚠ DOLLARS IS THE FALLBACK FOR EVERY UNREADABLE VALUE, including a flags blob
 * that is null because the preferences read failed. That is deliberate, and it
 * is the safe direction: dollars is the only unit that is never `null` for any
 * trade, so a screen falling back to it shows fewer blanks than one falling back
 * to R (null for every stop-less trade) ever could.
 *
 * ⚠ It also means a failed read looks exactly like a user who never touched the
 * picker. That is fine for READING. It is not fine for writing — see
 * `usePnlUnit`, which refuses to save while the blob is unread.
 */
export function readPnlUnit(appFlags: Record<string, unknown> | null | undefined): PnlUnit {
  const raw = (appFlags ?? {})[PNL_UNIT_FLAG];
  return isPnlUnit(raw) ? raw : 'usd';
}

/**
 * The whole flags blob with the unit replaced.
 *
 * 🚨 SPREAD, NOT REPLACE. `app_flags` is one JSONB column shared by every
 * surface — the planned-target unit, the journal layout, the leak loop, the
 * custom widgets, tour-seen markers. Returning `{ pnlDisplayUnit: unit }` would
 * type-check, save cleanly, and delete all of them. The caller must pass the
 * blob it READ, and must actually have read one.
 */
export function withPnlUnit(
  appFlags: Record<string, unknown> | null | undefined,
  unit: PnlUnit,
): Record<string, unknown> {
  return { ...(appFlags ?? {}), [PNL_UNIT_FLAG]: unit };
}
